import React, { useEffect, useState } from 'react';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import { useDispatch, useI18n, useSelector, useKitchenMessage, useWaste } from '~Hooks';
import Modal from '~ComponentsImproved/Modal';
import { addToast } from '~actions';
import { useReportsApi } from '~Components/ReportsUniverse';
import { useStorage } from '~Components/Storage';
import { useCompetition, SUBTRACT_SALE_TYPE } from '~Providers/CompetitionsProvider';
import RemoveItems from '~Components/RemoveItems';
import { useInventoryApi, useInventoryUniverse } from '~Components/InventoryUniverse';
interface Props {
  tableId: string;
  tableName: string;
  selectedItems: string[];
  clearChosen: () => void;
  closeModal: () => void;
}

const RemoveItemModal: React.FunctionComponent<Props> = ({
  tableId,
  tableName,
  selectedItems,
  clearChosen,
  closeModal,
}) => {
  const api = useTableApi();
  const reportsApi = useReportsApi();
  const [storage] = useStorage();
  const [isShowing, setIsShowing] = useState<boolean>(false);
  const competitions = useCompetition();
  const waste = useWaste();

  const universe = useTableUniverse();
  const cheque = universe.cheques.find((c) => c.tableId === tableId);
  const universeItems = universe.items;
  const sendKitchenMessage = useKitchenMessage();

  const staff = useSelector((state) => state?.staff);
  const venueId = useSelector((state) => state.venueId);

  const inventoryUniverse = useInventoryUniverse();
  const inventoryApi = useInventoryApi(venueId);

  useEffect(() => {
    setIsShowing(true);
  }, [cheque]);

  const handleSubmit = (message: string, commentOnWaste?: string) => {
    const itemsToRemove = universeItems.filter((item) => selectedItems.includes(item.id));
    const totalAmount = itemsToRemove.reduce((acc, item) => {
      return (acc += item.amount - item.discount);
    }, 0);

    api.removeItems(
      itemsToRemove,
      itemsToRemove[0].chequeId,
      message,
      tableId,
      Boolean(commentOnWaste),
    );

    if (commentOnWaste !== undefined) {
      const itemsToWaste = itemsToRemove.map((item) => ({
        itemId: item.itemId,
        fraction: item.fraction,
        title: item.title,
        message: item.message,
      }));
      waste.register(itemsToWaste, message, commentOnWaste);
    } else {
      /** Correction - add back inventory */
      itemsToRemove.forEach((item) => {
        const invItem = inventoryUniverse.items.find((i) => i.itemId === item.itemId);

        if (invItem && typeof invItem.count === 'number') {
          inventoryApi.addStock(invItem.itemId, item.fraction);
        }
      });
    }

    if (itemsToRemove.length > 0) {
      api.log.removeItems(itemsToRemove[0].chequeId, tableId, itemsToRemove, message);
    }

    // Remove item includes multiple items, This is correct for norweigan tax authorities
    reportsApi.orderLineCorrected('REMOVE_ITEM', totalAmount);

    // We subtract from the owner of the cheque, because a common scenario IRL is that the head waiter removes items from a cheque
    competitions.subtractSale({
      staffId: cheque?.owner,
      items: itemsToRemove,
      type: SUBTRACT_SALE_TYPE.REMOVE_ITEM,
    });

    if (storage.printVoidedItems) {
      sendKitchenMessage({
        type: 'VOID_ITEMS',
        tableName,
        staffName: staff?.name,
        message,
        items: itemsToRemove.map((item) => ({
          itemId: item.itemId,
          categoryId: item.categoryId,
          title: item.title,
          fraction: item.fraction,
          price: item.amount - item.discount,
        })),
      });
    }

    clearChosen();
  };

  return (
    <Modal isShowing={isShowing} hide={closeModal} position="flex-start">
      <RemoveItems
        chequeId={cheque?.id}
        itemIdsToRemove={selectedItems}
        onComplete={(message, commentOnWaste) => {
          handleSubmit(message, commentOnWaste);
        }}
      />
    </Modal>
  );
};

export default RemoveItemModal;
